// components/DialogProvider/index.tsx
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { PopupTransition } from "components/@extended/Transitions";
import React, { createContext, useContext, useState } from "react";

type DialogColors =
  | "error"
  | "inherit"
  | "success"
  | "primary"
  | "secondary"
  | "info"
  | "warning";

interface DialogOptions {
  title: string;
  message?: string | JSX.Element;
  icon?: React.ReactNode;
  button?: string;
  color?: DialogColors;
  iconColor?: string;
  iconBackgroundColor?: string;
}

interface PromiseInfo {
  resolve: (value: boolean | PromiseLike<boolean>) => void;
  reject: (reason?: any) => void;
}

type ShowDialogHandler = (options: DialogOptions) => Promise<boolean>;

// Create the context so we can use it in our App
const DialogContext = createContext<ShowDialogHandler>(() => {
  throw new Error("Component is not wrapped with a DialogProvider.");
});

const DialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DialogOptions>({
    title: "",
  });
  const [promiseInfo, setPromiseInfo] = useState<PromiseInfo>();
  const showDialog: ShowDialogHandler = (options) => {
    return new Promise<boolean>((resolve, reject) => {
      setPromiseInfo({ resolve, reject });
      setOptions(options);
      setOpen(true);
    });
  };
  const handleConfirm = () => {
    setOpen(false);
    promiseInfo?.resolve(true);
    setPromiseInfo(undefined);
  };
  const handleCancel = () => {
    setOpen(false);
    promiseInfo?.resolve(false);
    setPromiseInfo(undefined);
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        TransitionComponent={PopupTransition}
        maxWidth="xs"
      >
        <DialogContent>
          <Stack alignItems="center" spacing={3.5} sx={{ p: 2, pb: 0 }}>
            <Avatar
              color="error"
              sx={{
                width: 72,
                height: 72,
                fontSize: "1.75rem",
                backgroundColor:
                  options.iconBackgroundColor || "rgb(253,232,231)",
                color: options.iconColor || "rgb(240,62,52)",
              }}
            >
              {/* <DeleteFilled /> */}
              {options.icon}
            </Avatar>
            <Stack spacing={2}>
              <Typography
                variant="h4"
                align="center"
                sx={{ fontWeight: "bold" }}
              >
                {options.title}
                <Typography align="center">{options.message}</Typography>
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={2}
              sx={{ width: "100%" }}
              justifyContent={"stretch"}
            >
              <Button
                fullWidth
                onClick={handleCancel}
                color="secondary"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                fullWidth
                color={options.color}
                variant="contained"
                onClick={handleConfirm}
                autoFocus
              >
                {options.button}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      <DialogContext.Provider value={showDialog}>
        {children}
      </DialogContext.Provider>
    </>
  );
};

export const useDialog = () => {
  return useContext(DialogContext);
};

export default DialogProvider;
